'use strict';
/* ==========================================================================
   levels/map.js — the hub: the nursery's SEED SHELF (DESIGN.md §1–§2). A row of seed packets on a wooden shelf, one
   per lesson.json step (so a new step shows up by itself): a green wax-seal number, the step title in slab type, the
   tag in hand lettering and a little picture in the label window. A finished step gets an INHERITED stamp (its score
   when Game.progress.scores[id] = {best, of}); the first step not done yet gets a red pencil "next!" arrow.
   Mr. Mendoza stands at the left with a speech bubble. → Game.replace(new Levels[id]()). On a phone held upright the
   steps are full-width rows in the portrait dock (Dock.map) and the shelf is only the picture (no hit areas, no bubble).
   ========================================================================== */
(function () {
  const PL = () => window.Plate;
  const PK_W = 318, PK_H = 450, SHELF_Y = 846;
  // each packet's color band + picture, by position (a 5th step would reuse the first)
  const LOOK = [
    { color: '#4f86b8', pic: { trait: 'eyeModel', value: 'blue' } },
    { color: '#3f7040', pic: { trait: 'seedColour', value: 'green' } },
    { color: '#8a3b1e', pic: { img: 'magnifier', trait: 'seedShape', value: 'wrinkled' } },
    { color: '#7a4c9c', pic: { trait: 'flowerColour', value: 'violet' } },
  ];
  // Mr. Mendoza's line on the map (captions only)
  const SAY = {
    hello: 'Start with packet one. Every packet grows out of the one before it.',
    next: 'Good growth. The next packet is marked.',
    done: 'All four inherited. Go tell your grandma where blue came from.',
  };

  /* the packets, laid out from lesson.json steps (numbers count the steps that earn a stamp, like the home page) */
  function tags() {
    const S = (Game.lesson && Game.lesson.steps) || [], n = Math.max(1, S.length);
    const x0 = 520, x1 = 1640, gap = n > 1 ? (x1 - x0) / (n - 1) : 0, w = Math.min(PK_W, gap ? gap - 36 : PK_W);
    let k = 0;
    return S.map((s, i) => {
      const stamps = s.stamp !== false; if (stamps) k++;
      const look = LOOK[i % LOOK.length];
      return { id: s.id, n: stamps ? k : null, title: s.title, sub: s.tag || '', w, h: PK_H, x: n > 1 ? x0 + i * gap : 1080,
        y: SHELF_Y - PK_H / 2 - 6 + [0, -6, 4, -3][i % 4], rot: [-0.03, 0.02, -0.015, 0.025][i % 4], color: look.color, pic: look.pic };
    });
  }
  const nextStep = () => tags().find(tg => tg.n != null && !Game.progress.stamps[tg.id]);

  class MapScene extends Scene {
    enter() {
      const st = Game.progress.stamps, T_ = tags().filter(tg => tg.n != null);
      this.allDone = T_.every(tg => st[tg.id]);
      this.say = this.allDone ? SAY.done : T_.some(tg => st[tg.id]) ? SAY.next : SAY.hello;
      this.stampNew = Game._justStamped; Game._justStamped = null;
      Audio.play('paper', { gain: 0.5 });
      if (Game.MUSIC) Audio.music(Game.MUSIC, { gain: 0.3 });
    }
    open(id) {
      Audio.play('pop', { gain: 0.6 });
      const C = Levels[id];
      if (typeof C === 'function') Game.replace(new C());
      else UI.toast('coming soon');
    }
    keyDown(k) {
      const all = tags(), n = parseInt(k, 10);
      if (n >= 1 && n <= all.length) this.open(all[n - 1].id);
      if (k === 'Enter') this.open((nextStep() || all[0]).id);
    }

    draw() {
      UI.allLessons();                       // "← All lessons": back to the site's home page (core/ui.js)
      const t0 = this.enteredAt, t = this.t, docked = UI.docked(), Pl = PL(), C = Pl.C;
      Pl.backdrop(t0);
      Pl.greenhouse(1080, 470, 1500, 720, { seed: 9 });
      // the shelf: a plank on two brackets, the bench below
      paperRect(1080, SHELF_Y + 18, 1400, 36, C.wood, { seed: 31, depth: 1.4, boil: false });
      for (const bx of [470, 1690]) paperPiece(blobPts(22, 40, 32 + bx, 0.05, 16), bx, SHELF_Y + 70, C.woodDark, { seed: 33, depth: 0.8 });
      paperRect(960, 1040, 2000, 100, C.woodDark, { seed: 35, depth: 1, boil: false });
      Pl.vine(1860, 1000, 760, { seed: 17, side: -1, s: pop(T, t0 + 0.2, 0.5, 1.6) });
      Pl.label('THE SEED SHELF', 1080, 120, 54, { t0: t0 + 0.2, seed: 304, rot: -0.01 });
      Pl.twine(760, 70, 1400, 70, { sag: 18, seed: 305 });
      paperRect(1080, 198, 640, 56, C.label, { seed: 307, depth: 0.7, boil: false, rot: 0.006, s: pop(T, t0 + 0.4, 0.3, 1.8) });   // readable over the engraving
      hand('Mendoza Nursery · Homestead, Florida', 1080, 196, 36, t0 + 0.5, { color: C.leafDark, seed: 306 });

      // Mr. Mendoza + speech (the speech goes into the dock when docked)
      const rise = E.outBack(P(T, t0 + 0.2, t0 + 0.7), 1.4);
      Puppet.draw('mendoza', 200, 1080 + (1 - rise) * 500, 700, { facing: 1, t: T, env: t > 0.6 && t < 3 ? 0.25 + 0.25 * Math.sin(T * 17) : 0 });
      if (docked) Dock.map({ say: this.say, who: (LessonHooks.speakers && LessonHooks.speakers.mendoza || {}).name, open: id => this.open(id) });
      else if (t > 0.6) UI.speech(this.say, 300, 250, { side: 'left', w: 440, t0: t0 + 0.6 });

      const nx = nextStep();
      tags().forEach((tg, i) => {
        const hit = docked ? { hover: false, clicked: false } : UI._hit('tag' + i, tg.x, tg.y, tg.w, UI.tap(tg.h));
        if (hit.hover && !this['hov' + i]) Audio.play('paper', { gain: 0.2, rate: 1.3 });
        this['hov' + i] = hit.hover;
        const s = pop(T, t0 + 0.35 + i * 0.12, 0.4, 2.2);
        Pl.packet(tg.x, tg.y, tg.w, tg.h, { title: tg.title, sub: tg.sub, n: tg.n, color: tg.color, seed: 40 + i * 9, rot: tg.rot, s, hover: hit.hover, t0: t0 + 0.8 + i * 0.12, pic: tg.pic });
        if (Game.progress.stamps[tg.id]) {
          let ss = 1;
          if (this.stampNew === tg.id) {
            const at = t0 + 0.9;
            ss = pop(T, at, 0.18, 1.2) * (1 + 1.4 * (1 - P(T, at, at + 0.12)));
            if (T >= at && !this.stampPlayed) { this.stampPlayed = true; Audio.play('stamp'); }
          } else ss = pop(T, t0 + 0.8, 0.25, 1.5);
          const sc = Game.progress.scores && Game.progress.scores[tg.id];
          const lab = (Game.lesson.completeCard && Game.lesson.completeCard.title) || 'INHERITED';
          UI.stamp(sc && sc.best != null && sc.of ? `${sc.best}/${sc.of}` : lab, tg.x, tg.y + tg.h * 0.06, { rot: -0.2, s: ss * 0.46, color: C.red });
        }
        if (!this.allDone && !docked && nx && nx.id === tg.id) {   // docked: the dock row says next!
          const ns = pop(T, t0 + 1.4, 0.4), bob = Math.sin(T * 4) * 8;
          if (ns > 0) {
            g.save(); g.globalAlpha = ns;
            const ay = tg.y - tg.h / 2 - 18 - bob;
            inkArrow(tg.x + 70, ay - 80, tg.x + 10, ay, { w: 6, color: C.red, seed: 600 + i, bulge: -18 });
            hand('next!', tg.x + 120, ay - 96, 44, 0, { dur: 0.01, color: C.red, rot: 0.08, seed: 610 + i });
            g.restore();
          }
        }
        if (hit.clicked) this.open(tg.id);
      });
    }
  }
  MapScene.tags = () => tags().map(tg => ({ id: tg.id, x: tg.x, y: tg.y }));   // tools/integration.js clicks these

  Levels.map = MapScene;
})();
